import {createAsyncThunk} from "@reduxjs/toolkit";
import {CustomerDetails} from "../../../types";
import api from "../../../utils/constants/api";
import {ApiResponse} from "../../../types/APIResponse";
import {apiEndpoints} from "../../../utils/constants/apiEndpoints";

interface GetCustomerDetailsParams {
    offset?: number;
    limit?: number;
    searchValue?: string;
    searchField?: string;
    activityLogType?: string;
    startDate?: string;
    endDate?: string;
    claimStatus?: 'all' | 'draft-incomplete' | 'draft-completed' | 'not-paid' | 'overdue';
    paymentStatus?: 'all' | 'paid' | 'payment-failed' | 'never-paid' | 'overdue' | 'refunded';
    subFilter?: string;
}

export const getCustomerDetails = createAsyncThunk<
    {
        data: CustomerDetails[];
        counts: number;
        filteredCount: number;
        draftIncompleteUserCount?: number;
        draftCompletedUserCount?: number;
        notPaidUserCount?: number;
        overdueClaimUserCount?: number;
        overdueCount?: number;
        paidCount?: number;
        paymentFailedCount?: number;
        neverPaidCount?: number;
        overdueSoon7dCount?: number;
        overdueSoon3dCount?: number;
        overdueSoon1dCount?: number;
        paidOverdueCount?: number;
        otherNeverPaidCount?: number;
        refundedCount?: number;
        limit: number;
    },
    GetCustomerDetailsParams,
    { rejectValue: string }
    >(
    'customerDetails/getCustomerList',
    async ({ offset = 0, limit = 10, searchValue, searchField, activityLogType, startDate, endDate, claimStatus, paymentStatus, subFilter }, { rejectWithValue }) => {
        try {
            const requestData: any = {
                offset,
                limit,
                order: {
                    "user.createdAt": "DESC",
                }
            };

            if (searchValue && searchField) {
                requestData.searchValue = searchValue;
                requestData.searchField = searchField;
            }

            if (activityLogType) {
                requestData.activityLogType = activityLogType;
            }

            if (startDate && endDate) {
                requestData.startDate = startDate;
                requestData.endDate = endDate;
            }

            if (claimStatus) {
                requestData.claimStatus = claimStatus;
            }

            if (paymentStatus) {
                requestData.paymentStatus = paymentStatus;
            }

            if (subFilter) {
                requestData.subFilter = subFilter;
            }

            const response: ApiResponse<CustomerDetails[]> & {
                counts: number;
                filteredCount: number;
                draftIncompleteUserCount?: number;
                draftCompletedUserCount?: number;
                notPaidUserCount?: number;
                overdueClaimUserCount?: number;
                overdueCount?: number;
                paidCount?: number;
                paymentFailedCount?: number;
                neverPaidCount?: number;
                overdueSoon7dCount?: number;
                overdueSoon3dCount?: number;
                overdueSoon1dCount?: number;
                paidOverdueCount?: number;
                otherNeverPaidCount?: number;
                refundedCount?: number;
            } = await api.post(
                apiEndpoints.getCustomerList,
                {
                    data: requestData,
                }
            );

            return {
                data: response.data,
                counts: response.counts,
                filteredCount: response.filteredCount,
                draftIncompleteUserCount: response.draftIncompleteUserCount,
                draftCompletedUserCount: response.draftCompletedUserCount,
                notPaidUserCount: response.notPaidUserCount,
                overdueClaimUserCount: response.overdueClaimUserCount,
                overdueCount: response.overdueCount,
                paidCount: response.paidCount,
                paymentFailedCount: response.paymentFailedCount,
                neverPaidCount: response.neverPaidCount,
                overdueSoon7dCount: response.overdueSoon7dCount,
                overdueSoon3dCount: response.overdueSoon3dCount,
                overdueSoon1dCount: response.overdueSoon1dCount,
                paidOverdueCount: response.paidOverdueCount,
                otherNeverPaidCount: response.otherNeverPaidCount,
                refundedCount: response.refundedCount,
                limit
            };
        } catch (error: any) {
            return rejectWithValue(error?.message || 'Failed to fetch customerList');
        }
    }
);

export const sendCustomerReminder = createAsyncThunk<
    { success: boolean; message: string },
    { customerIds: string[]; emailType: string },
    { rejectValue: string }
>(
    'customerDetails/sendReminder',
    async ({ customerIds, emailType }, { rejectWithValue }) => {
        try {
            const response: ApiResponse<any> & { message: string } = await api.post(
                apiEndpoints.sendCustomerReminder,
                {
                    data: { customerIds, emailType },
                }
            );
            return { success: response.success, message: response.message };
        } catch (error: any) {
            return rejectWithValue(error?.message || 'Failed to send reminders');
        }
    }
);
